import type { CSSProperties } from 'react';

interface SessionWarningBodyProps {
  totalSeconds: number;
  isRefreshing: boolean;
  onReauth: () => void;
}

/**
 * Body of the session-expiry warning toast.
 *
 * Uses inline `style={...}` instead of Tailwind utility classes because
 * webapp ships a pre-built static Tailwind bundle that doesn't include
 * arbitrary-value classes (`bg-[#FEF9E7]`) — so the same JSX that works
 * pixel-perfect on postexchange / admin renders unstyled here unless
 * we deliver the styling via raw CSS.
 *
 * UI mirrors postexchange-webapp / domainboost_admin_FrontEnd byte-for-
 * byte; only the styling delivery mechanism is different.
 */
export const SessionWarningBody = ({
  totalSeconds,
  isRefreshing,
  onReauth,
}: SessionWarningBodyProps) => {
  const safe = Math.max(0, totalSeconds);
  const days = Math.floor(safe / 86400);
  const hours = Math.floor((safe % 86400) / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const pad2 = (n: number) => n.toString().padStart(2, '0');

  const numS: CSSProperties = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontWeight: 700,
    color: '#A16207',
    fontVariantNumeric: 'tabular-nums',
  };
  const unitS: CSSProperties = {
    color: '#A16207',
    fontWeight: 600,
    marginLeft: 2,
  };
  const spaceS: CSSProperties = { marginLeft: 6 };

  let countdown: React.ReactNode;
  if (days > 0) {
    countdown = (
      <>
        <span style={numS}>{days}</span>
        <span style={unitS}>d</span>
        <span style={{ ...numS, ...spaceS }}>{pad2(hours)}</span>
        <span style={unitS}>h</span>
      </>
    );
  } else if (hours > 0) {
    countdown = (
      <>
        <span style={numS}>{hours}</span>
        <span style={unitS}>h</span>
        <span style={{ ...numS, ...spaceS }}>{pad2(minutes)}</span>
        <span style={unitS}>min</span>
        <span style={{ ...numS, ...spaceS }}>{pad2(seconds)}</span>
        <span style={unitS}>sec</span>
      </>
    );
  } else if (minutes > 0) {
    countdown = (
      <>
        <span style={numS}>{minutes}</span>
        <span style={unitS}>min</span>
        <span style={{ ...numS, ...spaceS }}>{pad2(seconds)}</span>
        <span style={unitS}>sec</span>
      </>
    );
  } else {
    countdown = (
      <>
        <span style={numS}>{pad2(seconds)}</span>
        <span style={unitS}>sec</span>
      </>
    );
  }

  const iconWrap: CSSProperties = {
    flexShrink: 0,
    width: 36,
    height: 36,
    borderRadius: 9999,
    background: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.6)',
  };

  const button: CSSProperties = {
    marginTop: 8,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 700,
    color: '#fff',
    background: 'linear-gradient(90deg, #D97706 0%, #B45309 100%)',
    boxShadow: '0 2px 6px rgba(180,83,9,0.3)',
    border: 'none',
    cursor: isRefreshing ? 'not-allowed' : 'pointer',
    opacity: isRefreshing ? 0.6 : 1,
    transition: 'all 150ms ease',
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '2px 0',
      }}
    >
      <div style={iconWrap}>
        <svg
          style={{ width: 20, height: 20, color: '#A16207' }}
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        {/* line-height 1.25 = Tailwind `leading-tight` (admin's value).
            line-height 1.375 = `leading-snug` (admin's value).
            mt 2px = `mt-0.5`. Tightening these so the heading + body
            ride at the exact same vertical rhythm as the admin app. */}
        <p
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#020817',
            lineHeight: 1.25,
            margin: 0,
          }}
        >
          Session expiring
        </p>
        <p
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: '#6B7280',
            marginTop: 2,
            marginBottom: 0,
            lineHeight: 1.375,
          }}
        >
          {countdown}
          <span style={{ marginLeft: 6 }}>left — save your work.</span>
        </p>
        <button
          type="button"
          onClick={onReauth}
          disabled={isRefreshing}
          // session-warning-renew-btn applies the :hover gradient swap
          // (rule lives in src/styles/index.css next to the toast overrides).
          className="session-warning-renew-btn"
          style={button}
        >
          {isRefreshing ? (
            <>
              <svg
                style={{
                  width: 12,
                  height: 12,
                  animation: 'session-warning-spin 1s linear infinite',
                }}
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Renewing…
            </>
          ) : (
            <>
              <svg
                style={{ width: 12, height: 12 }}
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Stay signed in
            </>
          )}
        </button>
      </div>
    </div>
  );
};
